import {AddToBasketTrackingData} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {memo, useState} from 'react';
import {productInfoType} from '../../../features/OutfitSlider/OutfitSlider';
import {ProductTileProps} from '../../../features/ProductTile/ProductTile';
import {useProductIdFromSlug} from '../../../features/ProductUrl';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {ProductStreamTokenPagination} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {useShopTheLookScrollManager} from '../ShopTheLookScrollIntoView/useShopTheLookScrollManager';
import {RecommendationsView} from './RecommendationsView';
import {NextPageSkeleton, Skeleton} from './Skeleton';
import {useIsRecoRequestEnabled} from './utils/useIsRecoRequestEnabled';
import {
  LANES_PER_PAGE,
  useAllRecommendationsData,
  useNextRecommendationsData,
  useRecommendationsData,
} from './utils/useRecommendationsData';

export interface RecommendationsConfiguration {
  gridColumns?: number;
  uniqueKey?: string;
  compactMode?: boolean;
  ProductTileComponent?: React.FC<ProductTileProps>;
  showInlineOutfitSlider?: boolean;
  setRecoType?: (recoType: string) => void;
  preloadOutfitDetailPage?: VoidFunction;
  setAddToBasketTrackingData?: (params: AddToBasketTrackingData) => void;
}

export const Recommendations: React.FC<
  {
    onAddToBasketCtaClick?: ({
      productId,
      locationId,
      couponPromotionId,
    }: {
      productId: number;
      locationId?: string;
      couponPromotionId?: string;
    }) => void;
    className?: string;
    productInfo?: productInfoType;
  } & RecommendationsConfiguration
> = ({className, ...props}) => {
  const {shouldScrollShopTheLookIntoView} = useShopTheLookScrollManager();

  const {ref, requestEnabled} = useIsRecoRequestEnabled<HTMLDivElement>(
    shouldScrollShopTheLookIntoView,
  );

  const productIdFromSlug = useProductIdFromSlug();
  // NaN is a fallback to make the type check work, it should technically never happen if this is only used on ADP as expected
  const productId = productIdFromSlug || NaN;

  // The scroll to Shop the Look removes its query param again with a history replace, which also
  // resets history page state. Keep loading all lanes for this product so the recos don't
  // switch back to paging right under the user.
  const [shopTheLookProductId, setShopTheLookProductId] = useState(
    shouldScrollShopTheLookIntoView ? productIdFromSlug : undefined,
  );
  if (
    shouldScrollShopTheLookIntoView &&
    shopTheLookProductId !== productIdFromSlug
  ) {
    setShopTheLookProductId(productIdFromSlug);
  }
  const fetchAllLanes =
    shouldScrollShopTheLookIntoView ||
    (productIdFromSlug !== undefined &&
      shopTheLookProductId === productIdFromSlug);

  const {pages, isNextPageReady, mountNextPage} = useTokenBasedStreamPages({
    uniqueKey: `adpRecommendations-${productId}-${fetchAllLanes}`,
    useRequest: fetchAllLanes
      ? useAllRecommendationsData
      : useRecommendationsData,
    useNextRequest: useNextRecommendationsData,
  });

  return (
    <RecommendationsWrapper
      ref={ref}
      data-testid="recosFetchTrigger"
      className={className}
    >
      {requestEnabled || fetchAllLanes ? (
        <>
          {pages.map(page => (
            <TilesByPage
              key={`${fetchAllLanes}-${page.page}`}
              page={page}
              loadingComponent={
                page.page === 1 ? MemoSkeleton : MemoNextPageSkeleton
              }
              errorComponent={EmptyError}
              StreamTileList={RecommendationsView}
              {...props}
              gridColumns={props.gridColumns}
              productId={productId}
              positionOffset={(page.page - 1) * LANES_PER_PAGE}
            />
          ))}
          {isNextPageReady && (
            <ProductStreamTokenPagination
              mountNextPage={mountNextPage}
              rootMargin="500px"
            />
          )}
        </>
      ) : (
        <MemoSkeleton />
      )}
    </RecommendationsWrapper>
  );
};

const MemoSkeleton = memo(Skeleton);
const MemoNextPageSkeleton = memo(NextPageSkeleton);
const EmptyError = () => null;

const RecommendationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
